/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import P1 from '@base/P1/P1';
import H3 from '@base/H3/H3';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import ClockLoader from '@base/ClockLoader/ClockLoader';
import useInterval from '@hooks/useInterval';
import { useDispatchToStore } from '@hooks/reduxHooks';
import cookies from '@utils/cookies';
import pushRoute from '@utils/pushRoute';
import {
  purchaseSubscription,
  checkForPaymentStatusJuspay,
  savePurchasedSubscription,
  fetchPartPaymentStatus
} from '@actions/checkout';
import {
  fetchAccessControl,
  setPrePaymentSubscriptionUids
} from '@actions/auth';
import {
  sendPaymentSuccessfulEvent,
  sendPaymentFailureEvent,
  sendPaymentPendingEvent
} from '@events/sendAnalytics';
import { Spacings, breakpoints } from '@styles/index';

const ProgressWrapper = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  grid-column: 5/21;
  margin-top: 100px;
  margin-bottom: ${Spacings.SPACING_12B};
  padding: 40px 80px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: 1/9;
    padding: 16px;
  }
`;

const Title = styled(H3)`
  margin-top: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    text-align: center;
  }
`;

const Info = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_9B};
  text-align: center;
  width: 450px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
  }
`;

const sendPaymentEvents = (
  user,
  subscription,
  orderId,
  type,
  partPaymentInfo,
  selectedGoalDetails = null
) => {
  const isCombo = !!subscription?.value?.is_bundle_topology;
  const goalUID = subscription?.value.uid || '';
  const goalName = subscription?.value.name || '';
  const amount =
    subscription?.tax_breakdown.find((item) => item.label === 'Total')
      ?.amount || 0;
  const isUserSubscribed =
    user.plusSubscriptionUids?.indexOf(goalUID) > -1 || false;
  const duration = subscription?.duration || 0;
  const subscriptionType = subscription?.type;

  const gId = isCombo ? selectedGoalDetails?.uid : goalUID;
  const gName = isCombo ? selectedGoalDetails?.name : goalName;

  if (type === 'success') {
    sendPaymentSuccessfulEvent(
      gId,
      gName,
      duration,
      user.uid || '',
      user.username || '',
      amount,
      orderId,
      isUserSubscribed,
      subscriptionType,
      partPaymentInfo,
      isCombo,
      goalUID,
      goalName
    );
  } else if (type === 'failure') {
    sendPaymentFailureEvent(
      gId,
      gName,
      duration,
      user.uid || '',
      user.username || '',
      amount,
      orderId,
      isUserSubscribed,
      subscriptionType,
      partPaymentInfo,
      isCombo,
      goalUID,
      goalName
    );
  } else if (type === 'pending') {
    sendPaymentPendingEvent(
      gId,
      gName,
      duration,
      user.uid || '',
      user.username || '',
      amount,
      orderId,
      isUserSubscribed,
      subscriptionType,
      isCombo,
      goalUID,
      goalName
    );
  }
};

const PaymentProcessing = () => {
  const router = useRouter();
  const dispatch = useDispatchToStore();
  const selectedGoalDetails = useSelector(({ auth }) => auth.selectedGoal);
  const [paymentProcessingCompleted, setPaymentProcessingCompleted] = useState(
    false
  );
  const [statusUnknown, setStatusUnknown] = useState(false);
  const statusCheckCount = useRef(0);
  const orderId = useRef(router.query.order_id);
  const merchantPaymentCode = useRef(router.query.merchant_payment_code);

  const purchaseSubscriptionResponse = useSelector(
    ({ checkout }) => checkout.purchaseSubscriptionResponse
  );
  const paymentStatusResponse = useSelector(
    ({ checkout }) => checkout.paymentStatusResponse
  );
  const me = useSelector(({ auth }) => auth.me);

  useEffect(() => {
    if (orderId.current || merchantPaymentCode.current) {
      const gateway = merchantPaymentCode.current ? 5 : 2;
      dispatch(purchaseSubscription(orderId.current, gateway));
    }
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      if (
        paymentStatusResponse &&
        purchaseSubscriptionResponse &&
        !paymentProcessingCompleted
      ) {
        if (!purchaseSubscriptionResponse.is_pending) {
          // success - goal, bundle
          if (
            paymentStatusResponse.state === 2 &&
            purchaseSubscriptionResponse.user_subscription?.subscription
          ) {
            setPaymentProcessingCompleted(true);
            const goalUID =
              purchaseSubscriptionResponse.user_subscription.subscription.value
                .uid;
            const partPaymentInfo = {};
            if (
              purchaseSubscriptionResponse.is_partial &&
              purchaseSubscriptionResponse.is_partial_last_part_payment
            ) {
              const partPaymentStatus = await dispatch(
                fetchPartPaymentStatus(goalUID)
              );
              await dispatch(fetchAccessControl(goalUID));
              partPaymentInfo.isPartPartment = true;
              partPaymentInfo.optedPartPayments =
                partPaymentStatus?.result?.part_payments?.length;
              partPaymentInfo.currentPartPaymentIndex =
                partPaymentStatus?.result?.part_payments?.length;
              partPaymentInfo.isLastPartPayment = true;
            }
            sendPaymentEvents(
              me,
              purchaseSubscriptionResponse.user_subscription.subscription,
              orderId.current,
              'success',
              partPaymentInfo,
              selectedGoalDetails
            );

            const isCombo = !!purchaseSubscriptionResponse
              .bundle_user_subscriptions?.length;

            // save previous subscriptions to figure out whether the user is a first time subscriber
            dispatch(setPrePaymentSubscriptionUids(me.plusSubscriptionUids));

            dispatch(
              savePurchasedSubscription(
                goalUID,
                orderId.current,
                isCombo
                  ? purchaseSubscriptionResponse.bundle_user_subscriptions
                  : purchaseSubscriptionResponse.user_subscription.subscription
              )
            );

            if (isCombo) {
              pushRoute(
                '/combo/[comboSlug]/[comboUID]/subscribe/thank-you',
                `${purchaseSubscriptionResponse.user_subscription.subscription.value.relative_link.replace(
                  '/goal/',
                  '/combo/'
                )}/subscribe/thank-you`
              );
            } else {
              pushRoute(
                '/goal/[goalSlug]/[goalUID]/subscribe/thank-you',
                `${purchaseSubscriptionResponse.user_subscription.subscription.value.relative_link}/subscribe/thank-you`
              );
            }
          } else if (
            // failure - goal, bundle
            paymentStatusResponse.state === 1 &&
            purchaseSubscriptionResponse.subscription
          ) {
            const partPaymentInfo = {};
            const isCombo =
              purchaseSubscriptionResponse.subscription.value
                .is_bundle_topology;
            const extraParams =
              (purchaseSubscriptionResponse.are_credits_applied
                ? '&credsApplied=true'
                : '') +
              (purchaseSubscriptionResponse.referral?.code
                ? `&referral_code=${purchaseSubscriptionResponse.referral?.code}`
                : '');
            if (purchaseSubscriptionResponse.is_partial) {
              partPaymentInfo.isPartPartment = true;
              partPaymentInfo.isLastPartPayment =
                purchaseSubscriptionResponse.is_partial_last_part_payment ||
                false;
            }

            // Handling when payment status is coming failed from server.
            setPaymentProcessingCompleted(true);
            sendPaymentEvents(
              me,
              purchaseSubscriptionResponse.subscription,
              orderId.current,
              'failure',
              partPaymentInfo,
              selectedGoalDetails
            );
            if (isCombo) {
              pushRoute(
                `/combo/[comboSlug]/[comboUID]/subscribe/[subscriptionUID]?payment_status=failed${extraParams}`,
                `${purchaseSubscriptionResponse.subscription.value.relative_link.replace(
                  '/goal/',
                  '/combo/'
                )}/subscribe/${
                  purchaseSubscriptionResponse.subscription.uid
                }?payment_status=failed${extraParams}`
              );
            } else {
              pushRoute(
                `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]?payment_status=failed${extraParams}`,
                `${purchaseSubscriptionResponse.subscription.value.relative_link}/subscribe/${purchaseSubscriptionResponse.subscription.uid}?payment_status=failed${extraParams}`
              );
            }
          } else if (
            // success - goal, bundle (part payment)
            paymentStatusResponse.state === 2 &&
            purchaseSubscriptionResponse.is_partial &&
            !purchaseSubscriptionResponse.is_partial_last_part_payment
          ) {
            const goalUID = purchaseSubscriptionResponse.subscription.value.uid;
            const isCombo =
              purchaseSubscriptionResponse.subscription.value
                .is_bundle_topology;
            const partPaymentStatus = await dispatch(
              fetchPartPaymentStatus(goalUID)
            );
            await dispatch(fetchAccessControl(goalUID));
            const partPaymentInfo = {
              isPartPartment: true,
              optedPartPayments:
                partPaymentStatus?.result?.part_payments?.length,
              currentPartPaymentIndex: partPaymentStatus?.result?.part_payments?.findIndex(
                (item) => !item.payment
              ),
              isLastPartPayment: false
            };
            sendPaymentEvents(
              me,
              purchaseSubscriptionResponse.subscription,
              orderId.current,
              'success',
              partPaymentInfo,
              selectedGoalDetails
            );
            setPaymentProcessingCompleted(true);
            if (isCombo) {
              pushRoute(
                '/combo/[comboSlug]/[comboUID]/subscribe/[subscriptionUID]?part_payment_status=success',
                `${purchaseSubscriptionResponse.subscription.value.relative_link.replace(
                  '/goal/',
                  '/combo/'
                )}/subscribe/${
                  purchaseSubscriptionResponse.subscription.uid
                }?part_payment_status=success`
              );
            } else {
              pushRoute(
                '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]?part_payment_status=success',
                `${purchaseSubscriptionResponse.subscription.value.relative_link}/subscribe/${purchaseSubscriptionResponse.subscription.uid}?part_payment_status=success`
              );
            }
          }
        }
      }
    })();
  }, [
    dispatch,
    paymentStatusResponse,
    purchaseSubscriptionResponse,
    paymentProcessingCompleted,
    me
  ]);

  const checkPaymentStatus = useCallback(() => {
    if (!paymentProcessingCompleted && statusCheckCount.current < 8) {
      statusCheckCount.current += 1;
      let gateway = 2;
      let gatewayName = 'juspay';
      if (merchantPaymentCode.current) {
        gateway = 5;
        gatewayName = 'ebanx';
      }
      if (purchaseSubscriptionResponse?.state !== 2) {
        dispatch(purchaseSubscription(orderId.current, gateway));
      }
      dispatch(checkForPaymentStatusJuspay(orderId.current, gatewayName));
    } else {
      // Handling when not getting final confirmation from server.
      setPaymentProcessingCompleted(true);
      setStatusUnknown(true);
      sendPaymentEvents(
        me,
        purchaseSubscriptionResponse?.subscription,
        orderId.current,
        'pending',
        selectedGoalDetails
      );
    }
  }, [dispatch, purchaseSubscriptionResponse, paymentProcessingCompleted, me]);

  useInterval(checkPaymentStatus, !paymentProcessingCompleted ? 3000 : null);

  const backToPlus = () => {
    if (
      purchaseSubscriptionResponse?.subscription &&
      !purchaseSubscriptionResponse?.subscription?.value?.is_bundle_topology
    ) {
      cookies.setCookie(
        'selectedGoalUid',
        purchaseSubscriptionResponse.subscription.value.uid
      );
      pushRoute(
        '/goal/[goalSlug]/[goalUID]',
        purchaseSubscriptionResponse.subscription.value.relative_link
      );
    } else {
      pushRoute('/', '/');
    }
  };

  return (
    <ProgressWrapper>
      <ClockLoader />
      <Title>
        {statusUnknown
          ? 'It’s taking longer that usual'
          : 'Your payment is in progress'}
      </Title>
      <Info>
        {statusUnknown
          ? 'We will notify you once we get a confirmation from the payment provider. Kindly wait for sometime.'
          : 'Please do not refresh the page and wait while we are processing your payment. This can take a few minutes.'}
      </Info>
      {statusUnknown && <Button label="Back to Plus" onClick={backToPlus} />}
    </ProgressWrapper>
  );
};

export default PaymentProcessing;
